import * as React from "react";
import {
    Alert,
    AlertColor,
    Autocomplete,
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import ComponentLoader from "../../../../components/common/componentLoader";
import EmailViewer from "../../../../components/common/emailViewer";
import CclLoadingButton from "../../../../components/common/cclButtons/cclLoadingButton";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import CclGenericAccordian from "../../../../components/common/cclGenericAccordian";
import {
    useGetParticipantEmailTemplatesQuery,
    usePreviewNotificationMutation,
    useSendParticipantEmailMutation,
} from "../../../../services/cclTokenedSessionApi";
import { Participant } from "../../../../services/types/enterpriseParticipantApiTypes";
import { AccessEventParticipantEmailDetails } from "../../../../services/types/accessEventTypes";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { UserClaimsService } from "../../../../services/currentUserService/currentUserService";
import { isValidCcAddress, MAX_CC_ADDRESSES, parseCcAddresses } from "./ccAddresses";

interface SendParticipantEmailPanelProps {
    sessionId: string;
    sessionCode: string;
    participants: Participant[];
    allParticipants: boolean;
}

// English-only for V1 — no language picker exposed here even though the backend accepts
// a languageKey (see the OE participant email plan, decision #3).
const LANGUAGE_KEY = 1;

// Fixed-locale, case-insensitive collator so the Recipient List order doesn't vary with the
// viewer's browser locale; reused across compares (constructing one per compare is slow).
const emailCollator = new Intl.Collator("en", { sensitivity: "base" });
const compareByEmail = (a: Participant, b: Participant): number =>
    emailCollator.compare(a.emailAddress ?? "", b.emailAddress ?? "") || a.esiKey - b.esiKey;

const SendParticipantEmailPanel: React.FC<SendParticipantEmailPanelProps> = (props) => {
    const { data: templates, isLoading: templatesLoading } = useGetParticipantEmailTemplatesQuery(
        { esKey: props.sessionId, languageKey: LANGUAGE_KEY },
        { skip: !props.sessionId },
    );
    const [sendParticipantEmail, { isLoading: sendLoading }] = useSendParticipantEmailMutation();
    const { logEvent } = useLogAccessEvent();
    const [
        previewNotification,
        { data: preview, isLoading: previewLoading, isSuccess: previewIsSuccess },
    ] = usePreviewNotificationMutation();

    const [emailTypeId, setEmailTypeId] = React.useState<number>(0);
    const [subject, setSubject] = React.useState("");
    const [body, setBody] = React.useState("");
    // Courtesy copies. "Copy Others" is free text (e.g. "a@ccl.org; b@ccl.org") split into the
    // array the API expects (see parseCcAddresses). "Copy Myself" adds the signed-in user's own
    // email to the same CC list; parseCcAddresses de-duplicates if they also typed it.
    const [ccOthersText, setCcOthersText] = React.useState("");
    const [copyMyself, setCopyMyself] = React.useState(false);
    const currentUserEmail = React.useMemo(() => new UserClaimsService().GetCurrentUserEmail(), []);
    const ccOthers = React.useMemo(() => parseCcAddresses([ccOthersText]), [ccOthersText]);
    const cc = React.useMemo(
        () =>
            parseCcAddresses([
                ...(copyMyself && currentUserEmail ? [currentUserEmail] : []),
                ccOthersText,
            ]),
        [copyMyself, currentUserEmail, ccOthersText],
    );
    const [open, setOpen] = React.useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("success");
    const [snackbarMessage, setSnackbarMessage] = React.useState("");

    const selectedTemplate = templates?.find((t) => t.emailTypeId === emailTypeId);
    const [initialized, setInitialized] = React.useState(false);

    // Only Confirmed registrations receive these emails — mirrors the same allowlist enforced
    // server-side in ParticipantEmailService.SendEmailsAsync. Filtering here too keeps the
    // displayed recipient count and what's actually sent in sync, rather than showing a count
    // that includes Tentative/Pending/Cancelled registrations the backend will silently skip.
    const confirmedParticipants = React.useMemo(
        () => props.participants.filter((p) => p.registrationStatus === "Confirmed"),
        [props.participants],
    );
    const unconfirmedCount = props.participants.length - confirmedParticipants.length;
    // display order only: alphabetical by email (the Recipient List's CSS columns then flow
    // down the first column and on to the second); the send payload keeps its original order.
    // Memoized because this panel re-renders on every subject/body keystroke.
    const recipientListParticipants = React.useMemo(
        () => [...confirmedParticipants].sort(compareByEmail),
        [confirmedParticipants],
    );

    const handleTemplateChange = (newEmailTypeId: number | null) => {
        const id = newEmailTypeId ?? 0;
        setEmailTypeId(id);
        const template = templates?.find((t) => t.emailTypeId === id);
        setSubject(template?.subject ?? "");
        setBody(template?.body ?? "");
    };

    // The Autocomplete falls back to templates[0] for display before the admin ever picks
    // anything, but that's a visual default only — without this, emailTypeId/subject/body
    // state stays at their blank initial values, so the Subject/Content fields would show
    // empty even though the dropdown displays a template name.
    React.useEffect(() => {
        if (initialized || !templates || templates.length === 0) return;
        handleTemplateChange(templates[0].emailTypeId);
        setInitialized(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templates, initialized]);

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") return;
        setOpen(false);
    };

    // Read-only render of the current subject/body via the same preview pipeline the
    // Custom-session editor uses — no persistence, just a Clavin-rendered look at the content.
    // Works for "Write Your Own" (emailTypeId 0) too: CustomNotificationService.PreviewNotificationMetaData
    // falls back to an empty Body when there's no backing Cosmos document, rather than failing,
    // so free-form content previews through Clavin with no template boilerplate.
    // Merge tokens resolve against the first participant in the Recipient List below (same
    // alphabetical-by-email sort shown there), rather than the backend's own arbitrary pick, so
    // the preview always reflects a participant the admin can actually see selected.
    const handlePreview = () => {
        previewNotification({
            eskey: Number(props.sessionId),
            languageId: LANGUAGE_KEY,
            clavinId: emailTypeId,
            subject,
            content: body,
            status: 0,
            esiKey: recipientListParticipants[0]?.esiKey,
        });
    };

    // Unlike Custom's Reset (a server-side call that restores the *stored* Cosmos document),
    // nothing here is ever saved — this just discards the admin's edits and re-populates the
    // fields from the selected template's original fetched content.
    const handleReset = () => {
        handleTemplateChange(emailTypeId);
    };

    const [sentLocked, setSentLocked] = React.useState(false);

    // Any real edit to subject/body unlocks the buttons again — this fires on template
    // selection and Reset too (both go through setSubject/setBody), which is correct: a fresh
    // Reset after a send should be sendable again once its content differs from what was
    // already sent.
    React.useEffect(() => {
        setSentLocked(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subject, body]);

    const handleSend = () => {
        sendParticipantEmail({
            esKey: props.sessionId,
            request: {
                esiKeys: confirmedParticipants.map((p) => p.esiKey),
                languageKey: LANGUAGE_KEY,
                emailTypeId,
                subject,
                body,
                cc: cc.length > 0 ? cc : undefined,
            },
        })
            .unwrap()
            .then(() => {
                setSnackbarSeverity("success");
                setSnackbarMessage("Participant Emails Sent");
                setOpen(true);
                setSentLocked(true);
                const templateName = selectedTemplate?.description ?? "Write Your Own";
                confirmedParticipants.forEach((participant) => {
                    const evtData: AccessEventParticipantEmailDetails = {
                        esiKey: participant.esiKey.toString(),
                        imKey: participant.imKey.toString(),
                        email: participant.emailAddress,
                        templateName,
                    };
                    logEvent("ParticipantEmailSent", evtData);
                });
            })
            .catch(() => {
                setSnackbarSeverity("error");
                setSnackbarMessage("Failed to send email.");
                setOpen(true);
            });
    };

    if (templatesLoading) {
        return <ComponentLoader msg={"Loading Emails"} />;
    }

    // The API rejects the whole send (400) on any invalid or over-limit CC address, so block
    // Send here and flag the offending chips instead of letting it fail after the click.
    const hasInvalidCc = ccOthers.some((address) => !isValidCcAddress(address));
    const ccTooMany = cc.length > MAX_CC_ADDRESSES;
    const ccHelperText = hasInvalidCc
        ? "Remove or correct the highlighted addresses."
        : ccTooMany
          ? `A maximum of ${MAX_CC_ADDRESSES} CC addresses is allowed.`
          : "Optional. Separate addresses with semicolons, e.g. person1@example.com; person2@example.com";

    const canSend =
        confirmedParticipants.length > 0 &&
        subject.trim() !== "" &&
        body.trim() !== "" &&
        !hasInvalidCc &&
        !ccTooMany &&
        !sentLocked;
    // Preview/Reset only make sense once the admin has actually edited something away from
    // the selected template's original content (or, for "Write Your Own", typed anything at
    // all — its baseline is blank). sentLocked additionally holds all three buttons disabled
    // right after a successful send, until subject/body actually change again.
    const hasChanges =
        (subject !== (selectedTemplate?.subject ?? "") ||
            body !== (selectedTemplate?.body ?? "")) &&
        !sentLocked;

    const getHeaderActions = () => (
        <Stack direction={"row"} spacing={3} justifyContent="right" width={1} paddingRight={3}>
            <CclLoadingButton
                onClick={(evt: React.MouseEvent<HTMLButtonElement>) => {
                    evt.stopPropagation();
                    handleSend();
                }}
                disabled={!canSend}
                loading={sendLoading}
                mode={"primary"}
            >
                Send
            </CclLoadingButton>
            <CclLoadingButton
                onClick={(evt: React.MouseEvent<HTMLButtonElement>) => {
                    evt.stopPropagation();
                    handlePreview();
                }}
                disabled={!hasChanges}
                loading={previewLoading}
                mode={"primary"}
            >
                Preview
            </CclLoadingButton>
            <CclLoadingButton
                onClick={(evt: React.MouseEvent<HTMLButtonElement>) => {
                    evt.stopPropagation();
                    handleReset();
                }}
                disabled={!hasChanges}
                mode={"primary"}
            >
                Reset
            </CclLoadingButton>
        </Stack>
    );

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                    <CclGenericAccordian
                        headingText="Send Participants Email"
                        defaultExpanded={true}
                        summaryRightSideElement={getHeaderActions()}
                    >
                        <Stack spacing={3}>
                            <Alert severity="info" icon={false}>
                                <Typography variant="body2">
                                    <strong>Recipients:</strong>{" "}
                                    {props.allParticipants
                                        ? `All (${confirmedParticipants.length} confirmed participant${
                                              confirmedParticipants.length === 1 ? "" : "s"
                                          })`
                                        : `${confirmedParticipants.length} confirmed participant${
                                              confirmedParticipants.length === 1 ? "" : "s"
                                          } selected`}
                                    {unconfirmedCount > 0 &&
                                        ` — ${unconfirmedCount} unconfirmed participant${
                                            unconfirmedCount === 1 ? "" : "s"
                                        } excluded`}
                                </Typography>
                            </Alert>

                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <TextField
                                    id="participant-email-cc-others"
                                    size="small"
                                    label="Copy Others"
                                    placeholder="person1@example.com; person2@example.com"
                                    value={ccOthersText}
                                    onChange={(evt) => setCcOthersText(evt.target.value)}
                                    error={hasInvalidCc || ccTooMany}
                                    helperText={ccHelperText}
                                    fullWidth
                                />
                                <FormControlLabel
                                    sx={{ whiteSpace: "nowrap", mr: 0 }}
                                    title={currentUserEmail ?? "Your email address isn't available"}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={copyMyself}
                                            onChange={(evt) => setCopyMyself(evt.target.checked)}
                                            disabled={!currentUserEmail}
                                        />
                                    }
                                    label="Copy Myself"
                                />
                            </Stack>

                            <Box pt={2}>
                                <Autocomplete
                                    disablePortal
                                    id="combo-participant-email-template"
                                    onChange={(_, newValue) =>
                                        handleTemplateChange(newValue?.emailTypeId ?? 0)
                                    }
                                    value={selectedTemplate ?? templates?.[0] ?? null}
                                    options={templates ?? []}
                                    getOptionLabel={(option) => option.description}
                                    isOptionEqualToValue={(option, value) =>
                                        option.emailTypeId === value.emailTypeId
                                    }
                                    sx={{ width: 1 }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Template" size="small" />
                                    )}
                                />
                            </Box>

                            <Divider light />

                            <Stack spacing={2}>
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: "bold", marginLeft: 3 }}
                                >
                                    Subject
                                </Typography>
                                <Editor
                                    apiKey="y7y53muqd751xs3r3tgo3wey3uwqehd18hiyp1s2pjdprezv"
                                    value={subject}
                                    onEditorChange={(newValue: string, editor) => {
                                        setSubject(editor.getContent({ format: "text" }));
                                    }}
                                    init={{
                                        height: 200,
                                        menubar: false,
                                        inline: false,
                                        branding: false,
                                        suffix: ".min",
                                        plugins: [
                                            "print preview",
                                            "searchreplace visualblocks code",
                                            "paste code help properties",
                                        ],
                                        toolbar: "undo redo | properties | help",
                                        external_plugins: {
                                            properties: "/plugins/properties-plugin.js",
                                        },
                                        // OE-specific token menu — see .claude/token-menu-options.png.
                                        properties_menu_layout: "oe",
                                        contextmenu: "program participant user links",
                                        content_style:
                                            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                                    }}
                                />
                            </Stack>

                            <Divider light />

                            <Stack spacing={2}>
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: "bold", marginLeft: 3 }}
                                >
                                    Message Content
                                </Typography>
                                <Editor
                                    apiKey="y7y53muqd751xs3r3tgo3wey3uwqehd18hiyp1s2pjdprezv"
                                    value={body}
                                    onEditorChange={(newValue: string) => setBody(newValue)}
                                    init={{
                                        height: 400,
                                        menubar: "file edit view insert format table help",
                                        branding: false,
                                        external_plugins: {
                                            properties: "/plugins/properties-plugin.js",
                                        },
                                        // OE-specific token menu — see .claude/token-menu-options.png.
                                        properties_menu_layout: "oe",
                                        contextmenu: "program participant user links",
                                        menu: {
                                            insert: {
                                                title: "Insert",
                                                items: "image link inserttable | charmap | insertdatetime | program participant user links",
                                            },
                                            format: {
                                                title: "Format",
                                                items: "bold italic underline strikethrough superscript subscript codeformat | formats blockformats fontsizes align | forecolor backcolor | removeformat",
                                            },
                                        },
                                        plugins: [
                                            "advlist autolink lists link image charmap print preview",
                                            "searchreplace visualblocks code fullscreen",
                                            "insertdatetime table paste code help properties",
                                        ],
                                        toolbar:
                                            "undo redo | formatselect | properties | bold italic fontcolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
                                        content_style:
                                            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                                    }}
                                />
                            </Stack>

                            <CclAlertSnackbar
                                open={open}
                                onClose={handleClose}
                                severity={snackbarSeverity}
                                message={snackbarMessage}
                            />
                        </Stack>
                    </CclGenericAccordian>

                    <CclGenericAccordian headingText="Recipient List" defaultExpanded={true}>
                        {confirmedParticipants.length === 0 ? (
                            <Alert severity="info">No confirmed participants to email.</Alert>
                        ) : (
                            <Box sx={{ columnCount: { xs: 1, sm: 2 }, columnGap: 3 }}>
                                <List dense disablePadding>
                                    {recipientListParticipants.map((participant) => (
                                        <ListItem
                                            key={participant.esiKey}
                                            divider
                                            disableGutters
                                            sx={{ breakInside: "avoid" }}
                                        >
                                            <ListItemText
                                                primary={`${participant.firstName} ${participant.lastName}`}
                                                secondary={participant.emailAddress}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </CclGenericAccordian>
                </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
                <CclGenericAccordian headingText="Preview" defaultExpanded={true}>
                    {previewLoading && <ComponentLoader msg={"Loading Email Preview"} />}
                    {previewIsSuccess && (
                        <Stack spacing={2}>
                            <Typography variant="subtitle1">Subject: {preview?.subject}</Typography>
                            <EmailViewer
                                content={
                                    preview !== undefined && preview !== null
                                        ? preview.content
                                        : "No Data"
                                }
                                disableLinks={true}
                            />
                        </Stack>
                    )}
                    {preview === undefined && !previewLoading && (
                        <Box minHeight={300}>
                            <Alert severity="info">
                                {hasChanges
                                    ? "Click Preview to review your changes."
                                    : "Make a change to the subject or content to enable Preview."}
                            </Alert>
                        </Box>
                    )}
                </CclGenericAccordian>
            </Grid>
        </Grid>
    );
};

export default SendParticipantEmailPanel;
